import React from "react";
import { Reveal, SectionHeading } from "@/components/common/Reveal";
import { INVESTMENT_PRODUCTS } from "@/data";
import { useEnquiryForm } from "@/context/FormContext";
import { ArrowUpRight, ShieldAlert } from "lucide-react";

export default function InvestmentAccess() {
  const { openForm } = useEnquiryForm();
  return (
    <section id="investment" className="relative py-24 md:py-36 border-t border-white/5" data-testid="investment-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <SectionHeading label="Alternative Investments &amp; Financial-Product Access" title="Explore approved products through authorised channels." className="max-w-3xl" />
          <p className="mt-6 max-w-2xl text-base md:text-lg font-light text-zinc-400 leading-relaxed">
            Eligible clients may request private consultations concerning selected products offered by registered fund managers, portfolio managers, insurers, AMCs, issuers and other authorised providers.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {INVESTMENT_PRODUCTS.map((p, i) => (
            <Reveal key={p.title} delay={(i % 3) * 0.06}>
              <div className="card-hover h-full flex flex-col rounded-2xl border border-white/5 bg-[#0C0C10] p-7 hover:border-white/15" data-testid={`product-card-${i}`}>
                <h3 className="font-head text-xl font-medium">{p.title}</h3>
                <ul className="mt-4 space-y-1.5 flex-1">{p.items.map((it) => <li key={it} className="text-sm text-zinc-500">{it}</li>)}</ul>
                {p.note && <p className="mt-4 text-[11px] text-zinc-600 leading-relaxed border-t border-white/5 pt-3">{p.note}</p>}
                <button onClick={() => openForm("investment")} className="group mt-5 inline-flex items-center gap-2 text-sm text-[#00D1FF] hover:text-white transition-colors" data-testid={`product-cta-${i}`}>
                  {p.cta} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-8 text-xs text-zinc-600 leading-relaxed">
            Subject to investor eligibility, minimum-investment requirements, KYC, documentation, product availability, risk disclosures and provider acceptance. FundAI does not operate one-click investing, does not promise allocation and does not advertise guaranteed or projected returns.
          </p>
        </Reveal>

        {/* Financial relationship disclosure */}
        <Reveal>
          <div id="partners-fin" className="mt-14 rounded-2xl border border-white/10 bg-[#0A0A0E] p-8 md:p-10" data-testid="relationship-disclosure">
            <div className="flex items-center gap-3 mb-4"><ShieldAlert className="h-5 w-5 text-zinc-500" /><span className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500">Financial-Product Access Network</span></div>
            <p className="text-sm text-zinc-400 leading-relaxed max-w-3xl">
              Financial-product enquiries may be facilitated through the applicable registrations and approved business, distribution, referral or execution arrangements of Nexcore, its authorised director or an associated distribution entity, as accurately recorded for each relationship.
            </p>
            <p className="mt-5 text-sm text-zinc-500 leading-relaxed max-w-3xl">
              In this connection, a Director of Nexcore Ventures Private Limited is associated, in an individual capacity, as a business / distribution partner with the following organisations:
            </p>
            <div className="mt-4 flex flex-wrap gap-2.5" data-testid="associated-organisations">
              {["Motilal Oswal", "Nuvama Wealth", "Kotak Securities"].map((n) => (
                <span key={n} className="text-sm text-zinc-300 border border-white/10 rounded-full px-4 py-2">{n}</span>
              ))}
            </div>
            <p className="mt-6 text-[11px] text-zinc-600 leading-relaxed max-w-3xl">
              Names identify the relevant business, distribution, referral or execution relationship only. Their inclusion does not imply ownership, sponsorship, endorsement, funding commitment, product recommendation or approval of FundAI or any business applying through FundAI. These organisations are not investors in, funding partners of, sponsors of, backers of or guarantors of FundAI or Nexcore Ventures Private Limited, and are not committed to fund FundAI applicants. Any such association is held by the individual Director and not by Nexcore Ventures Private Limited unless separately documented. Logos are not displayed pending written permission. Products are offered and executed only by the respective registered provider or authorised intermediary, subject to eligibility, KYC, documentation and provider acceptance.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
